import { FC, useCallback, useEffect, useState } from "react";
import { BorrowingStats, LendingStats, LoanType } from "../../../types";
import NFTInfo from "../NFTInfo/NFTInfo";
import InlineCardStats from "../InlineCardStats/InlineCardStats";
import b from "../../../assets/assets/b.svg";
import {
  calculateAccruedInterestPercentage,
  convertSatToBtc,
} from "../../../utils";
import {
  defaultFloorPrice,
  defaultAPY,
  defaultBestOffer,
  defaultDuration,
  defaultInterest,
  defaultLTVRatio,
  defaultValue,
} from "../../../constants";
import Tooltip from "../Tooltip/Tooltip";
import { useGetOrderBook } from "../../../hooks/liquidium/useGetOrderBook";
import LoadingBar from "../LoadingBar/Loading";
import dropIcon from "../../../assets/icons/down-arrow.svg";

type rowType = "borrow" | "lend";

interface RowCardProps {
  disabled?: boolean;
  onClick: Function;
  type: rowType;
  offer: LoanType;
  isOrderBook?: boolean;
  open?: boolean;
  onToggle?: () => void;
  isBRC20?: boolean;
}

const RowCard: FC<RowCardProps> = ({
  disabled,
  onClick,
  type,
  offer,
  isOrderBook,
  open,
  onToggle,
  isBRC20,
}) => {
  const [stats, setStats] = useState<BorrowingStats | LendingStats>();
  const floorPrice = offer.collection ? offer.collection.floorPrice : 0;
  const showOrderBook = open;
  const {
    orderBook,
    loadingOrderBook,
    orderBookError,
    getOrderBook,
    recentActiveLoans,
  } = useGetOrderBook();

  const onFetchOrderBook = () => {
    if (onToggle) {
      onToggle();
    }
    if (!orderBook && offer.collectionSymbol) {
      getOrderBook({ collectionSymbol: offer.collectionSymbol });
      return;
    }
  };

  useEffect(() => {
    const tempFloor = convertSatToBtc(
      false,
      offer.collection ? offer.collection.floorPrice : 0,
    );
    const principal = convertSatToBtc(false, offer.principal);

    switch (type) {
      case "lend":
        setStats({
          bestOffer: { ...defaultBestOffer, value: principal },
          ...(isBRC20
            ? { value: { ...defaultValue, value: tempFloor } }
            : { floorPrice: { ...defaultFloorPrice, value: tempFloor } }),
          LTV: { ...defaultLTVRatio, value: offer.ltv },
          duration: { ...defaultDuration, value: offer.term },
          APY: { ...defaultAPY, value: offer.interestRate },
        });
        break;

      case "borrow":
        setStats({
          bestOffer: { ...defaultBestOffer, value: principal },
          ...(isBRC20
            ? { value: { ...defaultValue, value: tempFloor } }
            : { floorPrice: { ...defaultFloorPrice, value: tempFloor } }),
          LTV: { ...defaultLTVRatio, value: offer.ltv },
          duration: { ...defaultDuration, value: offer.term },
          interest: {
            ...defaultInterest,
            value: calculateAccruedInterestPercentage(
              offer.interestRate,
              offer.term,
            ).toFixed(2),
          },
        });
        break;
    }
  }, [type, offer, floorPrice]);

  return (
    <div
      className={`bg-gray-800 rounded-xl border border-white/20 sm:flex p-[10px] flex-col transition-all duration-300 ease-in-out overflow-hidden  ${
        isOrderBook
          ? "cursor-pointer hover:bg-zinc-800/70 hover:border-white/50"
          : ""
      } ${
        showOrderBook
          ? "h-[546px] sm:h-[495px] lg:h-[470px] xl:h-[470px] 2xl:h-[470px]"
          : "h-[115px] lg:h-[86px]"
      }`}
      key={offer.id}
      onClick={isOrderBook ? onFetchOrderBook : () => {}}
    >
      <div className="grid lg:grid-cols-3 grid-cols-2 justify-items-center w-full">
        <div className="justify-self-start">
          {offer.collection && (
            <>
              <div className="justify-self-start lg:block hidden">
                <NFTInfo
                  collectionName={offer.collection.name}
                  img={offer.collection.imageURI}
                  collectionVerified={true}
                />
              </div>
              <div className="justify-self-start lg:hidden ">
                <NFTInfo
                  collectionName={offer.collection.name}
                  img={offer.collection.imageURI}
                  collectionVerified={true}
                  small={true}
                />
              </div>
            </>
          )}
        </div>
        <div className="hidden lg:flex  sm:min-w-[500px] text-center items-center justify-center ">
          {stats ? (
            <InlineCardStats stats={stats} />
          ) : (
            <p className="text-md text-white font-bold">No offers available</p>
          )}
        </div>
        <div className="justify-self-end flex items-center">
          {isOrderBook && (
            <button>
              <img
                src={dropIcon}
                alt="Dropdown"
                className={`${
                  showOrderBook ? "rotate-180" : "rotate-0"
                } transition-all w-4 h-4 mr-4 duration-500 opacity-70 hover:opacity-100`}
              ></img>
            </button>
          )}
          <Tooltip
            content={
              type === "borrow" && disabled
                ? offer.principal
                  ? "You don’t have an Ordinal from this collection in your wallet"
                  : "No offers for this collection"
                : undefined
            }
          >
            <button
              className="bg-gray-500 lg:rounded-2xl rounded-lg text-gray-50 text-xs sm:text-sm font-black hover:bg-opacity-70 disabled:bg-gray-600 disabled:hover:bg-opacity-100 lg:p-3 p-1 border border-white/20 px-3 lg:h-auto h-full"
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                onClick(offer, stats);
              }}
            >
              <div className="">
                <p className="font-extrabold sm:text-sm text-xs text-gray-50">
                  {type === "lend" ? "Lend" : type === "borrow" && "Borrow"}
                </p>
              </div>
              {type === "borrow" && (
                <div className="lg:hidden block">
                  <div className={"text-center"}>
                    <div style={{ minWidth: "70px" }}></div>
                    <p className="font-extrabold text-sm text-gray-50 flex justify-center">
                      {convertSatToBtc(false, offer.principal)}{" "}
                      <img src={b} alt="" className="ml-[2px] mt-[1px]" />
                    </p>
                  </div>
                </div>
              )}
            </button>
          </Tooltip>
        </div>
      </div>
      <div className="flex justify-center w-full lg:hidden mt-3">
        <InlineCardStats stats={stats} small stretch />
      </div>
      <div
        className={`w-full sm:p-2 p-1  sm:grid-cols-2 sm:grid gap-4 sm:gap-4 h-full ${
          showOrderBook ? "opacity-100" : "opacity-0"
        } transition-all `}
      >
        <div className=" sm:mt-4 mt-2 flex flex-col">
          <p className="font-semibold text-zinc-50 text-sm sm:text-base text-center mb-3">
            Top Offers
          </p>
          {loadingOrderBook ? (
            <div
              className={`bg-blue-500/5 border border-white/20   rounded-md p-2 pb-0  sm:mr-4 flex  transition-all duration-500 ease-in-out  max-h-[170px] sm:max-h-[316px] w-full scrollbar-thin overflow-scroll  justify-center items-center  sm:text-sm text-xs text-zinc-400 animate-pulse h-[170px] sm:h-[322px]`}
            >
              Fetching...
            </div>
          ) : (
            <div
              className={`bg-blue-500/5 border border-white/20  text-zinc-200 rounded-md p-2 pb-0  sm:mr-4 h-[50vh]   transition-all duration-500 ease-in-out  max-h-[170px] sm:max-h-[316px] w-full scrollbar-thin overflow-scroll `}
            >
              {orderBook?.length && orderBook?.length > 0 ? (
                <table className="w-full font-bold ">
                  <thead className="text-left text-sm">
                    <tr>
                      <th className="md:w-[20%]">Rank</th>
                      <th key="amount">Amount</th>
                      <th key="created">Created</th>
                    </tr>
                  </thead>
                  <tbody className="font-medium text-zinc-400 text-sm overflow-y-scroll max-h-[200px]">
                    {orderBook?.map((entry, index) => (
                      <tr
                        className={`${
                          index !== orderBook.length - 1
                            ? "border-b border-white/10"
                            : ""
                        } h-7`}
                      >
                        <td className="md:w-[20%] ">{entry.rank}</td>
                        <td>₿ {entry.principle}</td>
                        <td>{entry.created}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center sm:text-sm text-xs text-zinc-600 h-full items-center flex justify-center w-full">
                  No Offers
                </p>
              )}
            </div>
          )}
        </div>
        <div className=" sm:mt-4 flex flex-col h-full pb-4 mt-3">
          <p className="font-semibold text-zinc-50 text-sm sm:text-base text-center mb-3">
            Active Loans
          </p>
          {loadingOrderBook ? (
            <div
              className={`bg-purple-500/5 border border-white/20  rounded-md p-2 pb-0  sm:mr-4  flex  transition-all duration-500 ease-in-out  max-h-[170px] sm:max-h-[316px] w-full scrollbar-thin overflow-scroll justify-center items-center  sm:text-sm text-xs text-zinc-400 animate-pulse h-[170px] sm:h-[322px]`}
            >
              Fetching...
            </div>
          ) : (
            <div
              className={`bg-purple-400/5 border border-white/20  text-zinc-200 rounded-md p-2 pb-0  sm:mr-4 h-full   transition-all duration-500 ease-in-out   max-h-[170px] sm:max-h-[316px] w-full scrollbar-thin overflow-scroll `}
            >
              {recentActiveLoans?.length && recentActiveLoans?.length > 0 ? (
                <table className="w-full font-bold">
                  <thead className="text-left text-sm">
                    <tr>
                      <th className="md:w-[20%]">No.</th>
                      <th>Amount</th>
                      <th>Started</th>
                    </tr>
                  </thead>
                  <tbody className="font-medium text-zinc-400 text-sm overflow-y-scroll max-h-[200px]">
                    {recentActiveLoans?.map((entry, index) => (
                      <tr
                        className={`${
                          index !== recentActiveLoans.length - 1
                            ? "border-b border-white/10"
                            : ""
                        } h-7`}
                      >
                        <td className="md:w-[20%] ">{entry.rank}</td>
                        <td>₿ {entry.principle}</td>
                        <td>{entry.started}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center sm:text-sm text-xs text-zinc-600 h-full items-center flex justify-center w-full ">
                  No Current Activity
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RowCard;
